import DownIcon from "@mui/icons-material/KeyboardArrowDown";
import LeftIcon from "@mui/icons-material/NavigateBefore";
import RightIcon from "@mui/icons-material/NavigateNext";
import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as CompanyListIcon } from "../assets/images/company-list-icon.svg";
import { ReactComponent as DashboardIcon } from "../assets/images/dashboard-icon.svg";
import { ReactComponent as EmployeeIcon } from "../assets/images/employee-icon.svg";
import Logo from "../assets/images/logo.png";
import Logo2 from "../assets/images/logo2.png";
import { ReactComponent as OrganogramIcon } from "../assets/images/organogram-icon.svg";
import { ReactComponent as SetaIcon } from "../assets/images/seta-icon.svg";
import { ReactComponent as TrainingCatalogueIcon } from "../assets/images/training-catalogue-icon.svg";
import { ReactComponent as TrainingStrategyIcon } from "../assets/images/training-strategy-icon.svg";
import { ReactComponent as TrainingSurveyIcon } from "../assets/images/training-survey-icon.svg";
import { ReactComponent as UserIcon } from "../assets/images/users-icon.svg";
import { ReactComponent as FillFormIcon } from "../assets/images/fill-form-icon.svg";
import { ReactComponent as ReviewFormIcon } from "../assets/images/review-form-icon.svg";
import { ReactComponent as CompanyCatalogueIcon } from "../assets/images/company-catalogue-icon.svg";
import { ReactComponent as IfundiCatalogueIcon } from "../assets/images/ifundi-catalogue-icon.svg";
import { ReactComponent as CriticalSkillsIcon } from "../assets/images/critical-skills-icon.svg";
import { ReactComponent as RequestedCatalogueIcon } from "../assets/images/requested-catalogue-icon.svg";
import { ReactComponent as AllocateCategoryIcon } from "../assets/images/allocate-category-icon.svg";
import { ReactComponent as SkillsPlannerIcon } from "../assets/images/skills-planner-icon.svg";
import { COMPANY_PRIVILEGED_ROLES, roleType, FEATURE_FLAGS } from "../constant/constants";
import CButton from "./CButton";
import { fetchConfiguration } from "../slices/configSlice";
import { fetchYearId } from "../slices/yearSlice";
import { refreshCurrentSdfUser } from "../slices/sdfSlice";
import {
  getCurrentUserRole,
  getSdfUserData,
  isCompanyPrivileged,
} from "../utils/roleUtils";
import { GridCloseIcon } from "@mui/x-data-grid";

function Sidebar({
  isOpen,
  setIsOpen,
  isMobileSidebarOpen,
  setIsMobileSidebarOpen,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const role = getCurrentUserRole();
  const { configData } = useSelector((state) => state.config);
  const [sdfUserProfile, setSdfUserProfile] = useState(() => getSdfUserData());
  const isActive = (path) => location.pathname === path;
  const [isTrainingSurveyOpen, setIsTrainingSurveyOpen] = useState(false);
  const [yearId, setYearId] = useState(null);

  const showPrivilegedTrainingForm = isCompanyPrivileged(role, sdfUserProfile);

  useEffect(() => {
    const syncSdfUserProfile = () => setSdfUserProfile(getSdfUserData());
    syncSdfUserProfile();
    window.addEventListener("sdfUserUpdated", syncSdfUserProfile);
    return () =>
      window.removeEventListener("sdfUserUpdated", syncSdfUserProfile);
  }, []);

  useEffect(() => {
    const refreshPrivilegedProfile = async () => {
      const res = await dispatch(refreshCurrentSdfUser());
      if (res.meta?.requestStatus === "fulfilled" && res.payload) {
        setSdfUserProfile(res.payload);
      }
    };
    refreshPrivilegedProfile();
  }, [dispatch, location.pathname]);

  // Check if training needs workflow is enabled
  const isTrainingWorkflowEnabled = configData?.training_need_workflow ?? false;

  // List of submenu paths
  const trainingSurveyPaths = [
    "/review-training-needs",
    "/training-configuration",
    "/form",
    "/employee-form-status",
    // Add other submenu paths here if needed
  ];

  const trainingCataloguePaths = [
    "/company-catalogue",
    "/ifundi-catalogue",
    "/critical-skills",
    "/training-catalogue",
    "/add-training-catalogue",
    "/assign-critical-training",
  ];

  useEffect(() => {
    // Fetch year ID and configuration data
    const loadConfiguration = async () => {
      try {
        // Check if user is logged in before making API calls
        const userData = JSON.parse(localStorage.getItem("sdfUser"));
        if (!userData?.company_id) {
          return; // Early return if user is not logged in (e.g., after logout)
        }

        const yearRes = await dispatch(fetchYearId());
        if (yearRes.payload) {
          setYearId(yearRes.payload);

          // Fetch configuration data
          const currentUserData = JSON.parse(localStorage.getItem("sdfUser"));
          if (currentUserData?.company_id) {
            dispatch(
              fetchConfiguration({
                yearId: yearRes.payload,
                companyId: currentUserData.company_id,
              })
            );
          }
        }
      } catch (error) {
        console.error("Error loading configuration:", error);
      }
    };

    loadConfiguration();
  }, [dispatch]);

  useEffect(() => {
    // Open submenu if current path matches any submenu route
    if (
      trainingSurveyPaths.some((path) => location.pathname.startsWith(path))
    ) {
      setIsTrainingSurveyOpen(true);
    } else if (
      isTrainingWorkflowEnabled &&
      (isCompanyPrivileged(role) ||
        role === roleType.MANAGER ||
        role === roleType.EMPLOYEE) &&
      isOpen
    ) {
      // Open Training Survey submenu by default when workflow is enabled and sidebar is open
      setIsTrainingSurveyOpen(true);
    }
  }, [location.pathname, isTrainingWorkflowEnabled, role, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsTrainingSurveyOpen(false);
    }
  }, [isOpen]);

  // Close mobile sidebar when navigating to a new page
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Mobile Overlay - Only visible on mobile when sidebar is open */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[999] md:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <div
        className={`z-[1000] h-screen fixed top-0 py-3 border-r border-solid border-blue-200 transition-all duration-300 bg-white ${
          isOpen
            ? "2xl:w-[300px] md:w-[250px] w-[300px] 2xl:px-6 px-4"
            : "2xl:w-[100px] w-[80px] px-4"
        } ${
          // Mobile behavior: show/hide based on isMobileSidebarOpen
          isMobileSidebarOpen ? "left-0" : "left-[-100%] md:left-0"
        }`}
      >
        {/* Desktop Toggle Button - Hidden on mobile */}
        <CButton
          onClick={() => setIsOpen(!isOpen)}
          className="hidden md:flex items-center bg-blue-75 hover:bg-blue-100 absolute right-[-20px] top-[16px] !p-1 border border-solid !border-blue-300 rounded-md w-[32px] !h-[32px]"
        >
          {isOpen ? (
            <LeftIcon className="text-blue-300 md:block hidden" />
          ) : (
            <RightIcon className="text-blue-300 md:block hidden" />
          )}
        </CButton>

        {/* Mobile Close Button - Only visible on mobile */}
        <CButton
          onClick={() => setIsMobileSidebarOpen(false)}
          className="md:hidden flex items-center bg-blue-75 hover:bg-blue-100 absolute right-4 top-4 !p-1 border border-solid !border-blue-300 rounded-md w-[28px] !h-[28px]"
        >
          <GridCloseIcon className="text-blue-300 !w-[20px] !h-[20px]" />
        </CButton>
        <div className="text-center pt-6">
          {isOpen ? (
            <img
              src={Logo}
              className="w-full 2xl:max-w-[180px] max-w-[150px] m-auto transition-all duration-300"
            />
          ) : (
            <img
              src={Logo2}
              className="w-full m-auto transition-all duration-300"
            />
          )}
        </div>
        <ul className="pb-5 2xl:pt-7 pt-5 max-h-[calc(100vh-110px)] overflow-y-auto">
          {/* Dashboard - Always first for overview */}
          {(isCompanyPrivileged(role) || role === roleType.MANAGER) &&
            isTrainingWorkflowEnabled && (
              <li className="group relative py-1">
                <NavLink
                  className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg ${
                    isActive("/dashboard")
                      ? "bg-blue-100 text-blue-600 font-medium"
                      : "bg-transparent text-gray-700 font-normal"
                  } ${
                    isOpen
                      ? "justify-start 2xl:px-4 px-4"
                      : "justify-center 2xl:px-4 px-2"
                  }`}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate("/dashboard");
                    setIsTrainingSurveyOpen(false);
                  }}
                >
                  <DashboardIcon
                    className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                      isActive("/dashboard") ? "fill-blue-600" : "fill-gray-700"
                    }`}
                    title="Dashboard"
                  />
                  <span
                    className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                      isOpen ? "block" : "hidden"
                    }`}
                  >
                    Dashboard
                  </span>
                </NavLink>
              </li>
            )}

          {/* ADMIN: Core Setup & Configuration */}
          {role === roleType.ADMIN && (
            <>
              <li className="group relative py-1">
                <NavLink
                  className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg ${
                    isActive("/company-list")
                      ? "bg-blue-100 text-blue-600 font-medium"
                      : "bg-transparent text-gray-700 font-normal"
                  } ${
                    isOpen
                      ? "justify-start 2xl:px-4 px-4"
                      : "justify-center 2xl:px-4 px-2"
                  }`}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate("/company-list");
                    setIsTrainingSurveyOpen(false);
                  }}
                >
                  <CompanyListIcon
                    className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                      isActive("/company-list")
                        ? "fill-blue-600"
                        : "fill-gray-700"
                    }`}
                    title="Companies"
                  />
                  <span
                    className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                      isOpen ? "block" : "hidden"
                    }`}
                  >
                    Companies
                  </span>
                </NavLink>
              </li>
              <li className="group relative py-1">
                <NavLink
                  className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg ${
                    isActive("/seta-list")
                      ? "bg-blue-100 text-blue-600 font-medium"
                      : "bg-transparent text-gray-700 font-normal"
                  } ${
                    isOpen
                      ? "justify-start 2xl:px-4 px-4"
                      : "justify-center 2xl:px-4 px-2"
                  }`}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate("/seta-list");
                    setIsTrainingSurveyOpen(false);
                  }}
                >
                  <SetaIcon
                    className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                      isActive("/seta-list")
                        ? "stroke-blue-600"
                        : "stroke-gray-700"
                    }`}
                    title="SETA Templates"
                  />
                  <span
                    className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                      isOpen ? "block" : "hidden"
                    }`}
                  >
                    SETA Templates
                  </span>
                </NavLink>
              </li>
              <li className="group relative py-1">
                <NavLink
                  className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg ${
                    isActive("/prompt-master")
                      ? "bg-blue-100 text-blue-600 font-medium"
                      : "bg-transparent text-gray-700 font-normal"
                  } ${
                    isOpen
                      ? "justify-start 2xl:px-4 px-4"
                      : "justify-center 2xl:px-4 px-2"
                  }`}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate("/prompt-master");
                    setIsTrainingSurveyOpen(false);
                  }}
                >
                  <SetaIcon
                    className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                      isActive("/prompt-master")
                        ? "stroke-blue-600"
                        : "stroke-gray-700"
                    }`}
                    title="Prompt Master"
                  />
                  <span
                    className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                      isOpen ? "block" : "hidden"
                    }`}
                  >
                    Prompt Master
                  </span>
                </NavLink>
              </li>
              <li className="group relative py-1">
                <NavLink
                  className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg ${
                    isActive("/eap-configuration")
                      ? "bg-blue-100 text-blue-600 font-medium"
                      : "bg-transparent text-gray-700 font-normal"
                  } ${
                    isOpen
                      ? "justify-start 2xl:px-4 px-4"
                      : "justify-center 2xl:px-4 px-2"
                  }`}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate("/eap-configuration");
                    setIsTrainingSurveyOpen(false);
                  }}
                >
                  <UserIcon
                    className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                      isActive("/eap-configuration")
                        ? "fill-blue-600"
                        : "fill-gray-700"
                    }`}
                    title="EAP Configuration"
                  />
                  <span
                    className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                      isOpen ? "block" : "hidden"
                    }`}
                  >
                    EAP Configuration
                  </span>
                </NavLink>
              </li>
            </>
          )}

          {/* SDF: Core Setup & Configuration */}
          {isCompanyPrivileged(role) && (
            <>
              <li className="group relative py-1">
                <NavLink
                  className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg ${
                    isActive("/employee-list")
                      ? "bg-blue-100 text-blue-600 font-medium"
                      : "bg-transparent text-gray-700 font-normal"
                  } ${
                    isOpen
                      ? "justify-start 2xl:px-4 px-4"
                      : "justify-center 2xl:px-4 px-2"
                  }`}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate("/employee-list");
                    setIsTrainingSurveyOpen(false);
                  }}
                >
                  <EmployeeIcon
                    className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                      isActive("/employee-list")
                        ? "fill-blue-600"
                        : "fill-gray-700"
                    }`}
                    title="Employees"
                  />
                  <span
                    className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                      isOpen ? "block" : "hidden"
                    }`}
                  >
                    Employees
                  </span>
                </NavLink>
              </li>
              {/* Only show Organogram if training workflow is enabled */}
              {isTrainingWorkflowEnabled && (
                <li className="group relative py-1">
                  <NavLink
                    className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg ${
                      isActive("/organogram-list")
                        ? "bg-blue-100 text-blue-600 font-medium"
                        : "bg-transparent text-gray-700 font-normal"
                    } ${
                      isOpen
                        ? "justify-start 2xl:px-4 px-4"
                        : "justify-center 2xl:px-4 px-2"
                    }`}
                    onClick={(event) => {
                      event.preventDefault();
                      navigate("/organogram-list");
                      setIsTrainingSurveyOpen(false);
                    }}
                  >
                    <OrganogramIcon
                      className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                        isActive("/organogram-list")
                          ? "fill-blue-600"
                          : "fill-gray-700"
                      }`}
                      title="Organogram"
                    />
                    <span
                      className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                        isOpen ? "block" : "hidden"
                      }`}
                    >
                      Organogram
                    </span>
                  </NavLink>
                </li>
              )}
            </>
          )}

          {/* SDF: Strategic Planning */}
          {isCompanyPrivileged(role) && isTrainingWorkflowEnabled && (
            <li className="group relative py-1">
              <NavLink
                className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg ${
                  isActive("/strategic-input-form")
                    ? "bg-blue-100 text-blue-600 font-medium"
                    : "bg-transparent text-gray-700 font-normal"
                } ${
                  isOpen
                    ? "justify-start 2xl:px-4 px-4"
                    : "justify-center 2xl:px-4 px-2"
                }`}
                onClick={(event) => {
                  event.preventDefault();
                  navigate("/strategic-input-form");
                  setIsTrainingSurveyOpen(false);
                }}
              >
                <TrainingStrategyIcon
                  className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                    isActive("/strategic-input-form")
                      ? "fill-blue-600"
                      : "fill-gray-700"
                  }`}
                  title="Training Strategy"
                />
                <span
                  className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                    isOpen ? "block" : "hidden"
                  }`}
                >
                  Training Strategy
                </span>
              </NavLink>
            </li>
          )}

          {/* Training Catalogues - Available to SDF and ADMIN */}
          {(isCompanyPrivileged(role) || role === roleType.ADMIN) && (
            <li className="group relative py-1">
              <div
                className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg bg-transparent text-gray-700 font-normal
                } ${
                  isOpen
                    ? "justify-start 2xl:px-4 px-4"
                    : "justify-center 2xl:px-4 px-2"
                }`}
              >
                <TrainingCatalogueIcon
                  className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] fill-gray-700
                  }`}
                  title="Training Catalogues"
                />
                <span
                  className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                    isOpen ? "block" : "hidden"
                  }`}
                >
                  Training Catalogues
                </span>
              </div>

              {/* Submenu - always show when sidebar is open */}
              {isOpen && (
                <div className="bg-white rounded-b-md">
                  <ul className="flex flex-col pl-8">
                    {/* Company Catalogue - Only for SDF */}
                    {isCompanyPrivileged(role) && (
                      <li className="group relative py-1">
                        <NavLink
                          to="/company-catalogue"
                          className={`flex gap-2 items-center 2xl:px-4 px-2 py-2 text-sm leading-sm block hover:bg-blue-50 cursor-pointer !rounded-md ${
                            isActive("/company-catalogue")
                              ? "bg-blue-100 text-blue-600 font-medium"
                              : "text-gray-700"
                          }`}
                        >
                          <CompanyCatalogueIcon
                            className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                              isActive("/company-catalogue")
                                ? "fill-blue-600"
                                : "fill-gray-700"
                            }`}
                          />
                          <span className="block">Company Catalogue</span>
                        </NavLink>
                      </li>
                    )}

                    {/* iFundi Catalogue - Available to SDF and ADMIN */}
                    <li className="group relative py-1">
                      <NavLink
                        to="/ifundi-catalogue"
                        className={`flex gap-2 items-center 2xl:px-4 px-2 py-2 text-sm leading-sm block hover:bg-blue-50 cursor-pointer !rounded-md ${
                          isActive("/ifundi-catalogue")
                            ? "bg-blue-100 text-blue-600 font-medium"
                            : "text-gray-700"
                        }`}
                      >
                        <IfundiCatalogueIcon
                          className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                            isActive("/ifundi-catalogue")
                              ? "fill-blue-600"
                              : "fill-gray-700"
                          }`}
                        />
                        <span className="block">iFundi Catalogue</span>
                      </NavLink>
                    </li>

                    {/* Critical Skills - Only for SDF */}
                    {isCompanyPrivileged(role) && (
                      <li className="group relative py-1">
                        <NavLink
                          to="/critical-skills"
                          className={`flex gap-2 items-center 2xl:px-4 px-2 py-2 text-sm leading-sm block hover:bg-blue-50 cursor-pointer !rounded-md ${
                            isActive("/critical-skills") ||
                            isActive("/assign-critical-training")
                              ? "bg-blue-100 text-blue-600 font-medium"
                              : "text-gray-700"
                          }`}
                        >
                          <CriticalSkillsIcon
                            className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                              isActive("/critical-skills")
                                ? "fill-blue-600"
                                : "fill-gray-700"
                            }`}
                          />
                          <span className="block">Critical Skills</span>
                        </NavLink>
                      </li>
                    )}

                    {isCompanyPrivileged(role) && (
                      <li className="group relative py-1">
                        <NavLink
                          to="/requested-training"
                          className={`flex gap-2 items-center 2xl:px-4 px-2 py-2 text-sm leading-sm block hover:bg-blue-50 cursor-pointer !rounded-md ${
                            isActive("/requested-training")
                              ? "bg-blue-100 text-blue-600 font-medium"
                              : "text-gray-700"
                          }`}
                        >
                          <RequestedCatalogueIcon
                            className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                              isActive("/requested-training")
                                ? "fill-blue-600"
                                : "fill-gray-700"
                            }`}
                          />
                          <span className="block">Training Not in Catalogue</span>
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </li>
          )}

          {/* Training Survey - Only when workflow is enabled */}
          {(isCompanyPrivileged(role) ||
            role === roleType.MANAGER ||
            role === roleType.EMPLOYEE) &&
            isTrainingWorkflowEnabled &&
            isOpen && (
              <li className="group relative py-1">
                <div
                  className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg bg-transparent text-gray-700 font-normal
                  ${
                    isOpen
                      ? "justify-start 2xl:px-4 px-4"
                      : "justify-center 2xl:px-4 px-2"
                  }`}
                >
                  <TrainingSurveyIcon
                    className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] fill-gray-700`}
                    title="Training Survey"
                  />
                  <span
                    className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                      isOpen ? "block" : "hidden"
                    }`}
                  >
                    Training Survey
                  </span>
                </div>

                {/* Submenu - only show when sidebar is open */}
                <div
                  className={`overflow-hidden transition-all duration-300 bg-white rounded-b-md ${
                    isTrainingSurveyOpen && isOpen ? "max-h-full" : "max-h-full"
                  }`}
                >
                  <ul className="flex flex-col pl-8">
                    {/* {role !== roleType.SDF && ( */}
                    {/* {` data : ${getSdfUserData()?.data}, role : ${getSdfUserData()?.role}, role !== roleType.SDF : ${role !== roleType.SDF}, role === roleType.SDF || role === roleType.COMPANY_ADMIN && typeof getSdfUserData()?.data === 'object' : ${role === roleType.SDF || role === roleType.COMPANY_ADMIN && typeof getSdfUserData()?.data === 'object'}`}
                      {(role !== roleType.SDF || (isCompanyPrivileged(role) && getSdfUserData()?.data != null)) && ( */}
                    {!isCompanyPrivileged(getCurrentUserRole()) && (
                      <li className="group relative py-1">
                        <NavLink
                          className={`flex gap-2 items-center 2xl:px-4 px-2 py-2 text-sm leading-sm block hover:bg-blue-50 cursor-pointer !rounded-md ${
                            isActive("/employee-form-status")
                              ? "bg-blue-100 text-blue-600 font-medium"
                              : "text-gray-700"
                          }`}
                          onClick={(event) => {
                            event.preventDefault();
                            navigate("/employee-form-status");
                          }}
                        >
                          <FillFormIcon
                            className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                              isActive("/employee-form-status")
                                ? "fill-blue-600"
                                : "fill-gray-700"
                            }`}
                          />
                          <span className="block">Training Needs Form</span>
                        </NavLink>
                      </li>
                    )}

                    {showPrivilegedTrainingForm && (
                        <li className="group relative py-1">
                          <NavLink
                            className={`flex gap-2 items-center 2xl:px-4 px-2 py-2 text-sm leading-sm block hover:bg-blue-50 cursor-pointer !rounded-md ${
                              isActive("/employee-form-status")
                                ? "bg-blue-100 text-blue-600 font-medium"
                                : "text-gray-700"
                            }`}
                            onClick={(event) => {
                              event.preventDefault();
                              navigate("/employee-form-status");
                            }}
                          >
                            <FillFormIcon
                              className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                                isActive("/employee-form-status")
                                  ? "fill-blue-600"
                                  : "fill-gray-700"
                              }`}
                            />
                            <span className="block">Training Needs Form</span>
                          </NavLink>
                        </li>
                      )}
                    {/* )} */}
                    {/* )} */}
                    {role !== roleType.EMPLOYEE && (
                      <li className="group relative py-1">
                        <NavLink
                          className={`flex gap-2 items-center 2xl:px-4 px-2 py-2 text-sm leading-sm block hover:bg-blue-50 cursor-pointer !rounded-md ${
                            isActive("/review-training-needs")
                              ? "bg-blue-100 text-blue-600 font-medium"
                              : "text-gray-700"
                          }`}
                          onClick={(event) => {
                            event.preventDefault();
                            navigate("/review-training-needs");
                          }}
                        >
                          <ReviewFormIcon
                            className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                              isActive("/review-training-needs")
                                ? "fill-blue-600"
                                : "fill-gray-700"
                            }`}
                          />
                          <span className="block">Manage Training Needs</span>
                        </NavLink>
                      </li>
                    )}
                    {isCompanyPrivileged(role) && (
                      <li className="group relative py-1">
                        <NavLink
                          className={`flex gap-2 items-center 2xl:px-4 px-2 py-2 text-sm leading-sm block hover:bg-blue-50 cursor-pointer !rounded-md ${
                            isActive("/bulk-training-allocation")
                              ? "bg-blue-100 text-blue-600 font-medium"
                              : "text-gray-700"
                          }`}
                          onClick={(event) => {
                            event.preventDefault();
                            navigate("/bulk-training-allocation");
                          }}
                        >
                          <AllocateCategoryIcon
                            className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                              isActive("/bulk-training-allocation")
                                ? "stroke-blue-600"
                                : "stroke-gray-700"
                            }`}
                          />
                          <span className="block">
                            Bulk Training Allocation
                          </span>
                        </NavLink>
                      </li>
                    )}
                    {isCompanyPrivileged(role) && (
                      <li className="group relative py-1">
                        <NavLink
                          className={`flex gap-2 items-center 2xl:px-4 px-2 py-2 text-sm leading-sm block hover:bg-blue-50 cursor-pointer !rounded-md ${
                            isActive("/employee-training-import")
                              ? "bg-blue-100 text-blue-600 font-medium"
                              : "text-gray-700"
                          }`}
                          onClick={(event) => {
                            event.preventDefault();
                            navigate("/employee-training-import");
                          }}
                        >
                          <RequestedCatalogueIcon
                            className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                              isActive("/employee-training-import")
                                ? "fill-blue-600"
                                : "fill-gray-700"
                            }`}
                          />
                          <span className="block">Employee Training Import</span>
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </div>
              </li>
            )}

          {/* Training Survey Submenu Items - Show when sidebar is collapsed and workflow is enabled */}
          {(isCompanyPrivileged(role) ||
            role === roleType.MANAGER ||
            role === roleType.EMPLOYEE) &&
            isTrainingWorkflowEnabled &&
            !isOpen && (
              <>
                {/* {role !== roleType.SDF && ( */}
                {(!isCompanyPrivileged(role) || showPrivilegedTrainingForm) && (
                <li className="group relative py-1">
                  <NavLink
                    className={`flex gap-2 items-center justify-center 2xl:px-4 px-2 py-3 rounded-lg ${
                      isActive("/employee-form-status")
                        ? "bg-blue-100 text-blue-600 font-medium"
                        : "bg-transparent text-gray-700 font-normal"
                    }`}
                    onClick={(event) => {
                      event.preventDefault();
                      navigate("/employee-form-status");
                    }}
                    title="Training Needs Form"
                  >
                    <FillFormIcon
                      className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                        isActive("/employee-form-status")
                          ? "fill-blue-600"
                          : "fill-gray-700"
                      }`}
                    />
                  </NavLink>
                </li>
                )}
                {/* )} */}
                {role !== roleType.EMPLOYEE && (
                  <li className="group relative py-1">
                    <NavLink
                      className={`flex gap-2 items-center justify-center 2xl:px-4 px-2 py-3 rounded-lg ${
                        isActive("/review-training-needs")
                          ? "bg-blue-100 text-blue-600 font-medium"
                          : "bg-transparent text-gray-700 font-normal"
                      }`}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate("/review-training-needs");
                      }}
                      title="Manage Training Needs"
                    >
                      <ReviewFormIcon
                        className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                          isActive("/review-training-needs")
                            ? "fill-blue-600"
                            : "fill-gray-700"
                        }`}
                      />
                    </NavLink>
                  </li>
                )}
                {isCompanyPrivileged(role) && (
                  <li className="group relative py-1">
                    <NavLink
                      className={`flex gap-2 items-center justify-center 2xl:px-4 px-2 py-3 rounded-lg ${
                        isActive("/employee-training-import")
                          ? "bg-blue-100 text-blue-600 font-medium"
                          : "bg-transparent text-gray-700 font-normal"
                      }`}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate("/employee-training-import");
                      }}
                      title="Employee Training Import"
                    >
                      <RequestedCatalogueIcon
                        className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                          isActive("/employee-training-import")
                            ? "fill-blue-600"
                            : "fill-gray-700"
                        }`}
                      />
                    </NavLink>
                  </li>
                )}
              </>
            )}

          {/* SDF: Advanced Planning Features */}
          {isCompanyPrivileged(role) && (
            <>
              <li className="group relative py-1">
                <NavLink
                  className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg ${
                    isActive("/generate-training")
                      ? "bg-blue-100 text-blue-600 font-medium"
                      : "bg-transparent text-gray-700 font-normal"
                  } ${
                    isOpen
                      ? "justify-start 2xl:px-4 px-4"
                      : "justify-center 2xl:px-4 px-2"
                  }`}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate("/generate-training");
                    setIsTrainingSurveyOpen(false);
                  }}
                >
                  <SkillsPlannerIcon
                    className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                      isActive("/generate-training")
                        ? "fill-blue-600"
                        : "fill-gray-700"
                    }`}
                    title="Skills Planner"
                  />
                  <span
                    className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                      isOpen ? "block" : "hidden"
                    }`}
                  >
                    Skills Planner
                  </span>
                </NavLink>
              </li>
              {FEATURE_FLAGS.SHOW_PLAN_OPTIMIZER_MENU && (
                <li className="group relative py-1">
                  <NavLink
                    className={`flex gap-2 items-center 2xl:px-4 px-2 py-3 rounded-lg ${
                      isActive("/plan-optimizer")
                        ? "bg-blue-100 text-blue-600 font-medium"
                        : "bg-transparent text-gray-700 font-normal"
                    } ${
                      isOpen
                        ? "justify-start 2xl:px-4 px-4"
                        : "justify-center 2xl:px-4 px-2"
                    }`}
                    onClick={(event) => {
                      event.preventDefault();
                      navigate("/plan-optimizer");
                      setIsTrainingSurveyOpen(false);
                    }}
                  >
                    <EmployeeIcon
                      className={`2xl:w-[24px] 2xl:h-[24px] w-[20px] h-[20px] ${
                        isActive("/plan-optimizer")
                          ? "fill-blue-600"
                          : "fill-gray-700"
                      }`}
                      title="Plan Optimizer"
                    />
                    <span
                      className={`2xl:text-base 2xl:leading-base text-sm leading-sm ${
                        isOpen ? "block" : "hidden"
                      }`}
                    >
                      Plan Optimizer
                    </span>
                  </NavLink>
                </li>
              )}
            </>
          )}
        </ul>
      </div>
    </>
  );
}

export default Sidebar;
